import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";

import { useOneTrustBannerVisible } from "./useOneTrustBannerVisible";

const MS_PER_MONTH = 30 * 24 * 60 * 60 * 1000;

/**
 * Set by the (externally managed) Rewards system on login and cleared on
 * logout. A non-empty value means the visitor is a logged-in Rewards member.
 */
const REWARDS_FIRST_NAME_COOKIE = "firstName";

const isRewardsMemberLoggedIn = (): boolean => {
    try {
        return !!Cookies.get(REWARDS_FIRST_NAME_COOKIE);
    } catch {
        return false;
    }
};

const PERSISTENT_DISMISS_KEY_SUFFIX = ":do-not-ask-until";

const isModalDismissed = (modalKey: string): boolean => {
    if (!modalKey) {
        return false;
    }

    try {
        return sessionStorage.getItem(modalKey) === "dismissed";
    } catch {
        return false;
    }
};

const markModalDismissed = (modalKey: string): void => {
    if (!modalKey) {
        return;
    }

    sessionStorage.setItem(modalKey, "dismissed");
};

const isModalPersistentlyDismissed = (modalKey: string): boolean => {
    if (!modalKey) {
        return false;
    }

    try {
        const raw = localStorage.getItem(
            `${modalKey}${PERSISTENT_DISMISS_KEY_SUFFIX}`,
        );
        if (!raw) {
            return false;
        }
        const expiry = parseInt(raw, 10);
        if (Number.isNaN(expiry)) {
            return false;
        }
        return Date.now() < expiry;
    } catch {
        return false;
    }
};

const markModalPersistentlyDismissed = (
    modalKey: string,
    durationMonths: number,
): void => {
    if (!modalKey || durationMonths <= 0) {
        return;
    }

    try {
        const expiry = Date.now() + durationMonths * MS_PER_MONTH;
        localStorage.setItem(
            `${modalKey}${PERSISTENT_DISMISS_KEY_SUFFIX}`,
            String(expiry),
        );
    } catch {
        // localStorage unavailable
    }
};

export const usePopupModalIsOpen = (
    id: string,
    delaySeconds: number | null,
    hasContent: boolean,
    dismissalDurationMonths: number,
    hideForRewardsMembers: boolean,
): [boolean, (open: boolean) => void, boolean, (checked: boolean) => void] => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);
    const [doNotAskAgain, setDoNotAskAgain] = useState(false);
    const isOneTrustBannerVisible = useOneTrustBannerVisible();

    const modalKey = id || "";
    const delayMs = (delaySeconds || 6) * 1000;

    useEffect(() => {
        if (!hasContent || !modalKey || hasTriggered) {
            return;
        }

        if (isModalDismissed(modalKey)) {
            return;
        }

        if (isModalPersistentlyDismissed(modalKey)) {
            return;
        }

        if (isOneTrustBannerVisible !== false) {
            return;
        }

        if (hideForRewardsMembers && isRewardsMemberLoggedIn()) {
            return;
        }

        const timer = setTimeout(() => {
            setIsOpen(true);
            setHasTriggered(true);
        }, delayMs);

        return () => {
            clearTimeout(timer);
        };
    }, [
        delayMs,
        hasContent,
        hasTriggered,
        hideForRewardsMembers,
        isOneTrustBannerVisible,
        modalKey,
    ]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            if (!open && hasTriggered) {
                if (doNotAskAgain && dismissalDurationMonths > 0) {
                    markModalPersistentlyDismissed(
                        modalKey,
                        dismissalDurationMonths,
                    );
                }
                markModalDismissed(modalKey);
            }
        },
        [doNotAskAgain, dismissalDurationMonths, hasTriggered, modalKey],
    );

    return [isOpen, handleOpenChange, doNotAskAgain, setDoNotAskAgain];
};
